import type {
  ContentBlock,
  MarkdownBlock,
  NotFoundData,
  ClarificationNeededData,
  UnsupportedRequestData,
  AgentErrorData,
  OrderSearchResultsData,
  OrderSummaryData,
  OrderTimelineData,
  OrderShipmentsData,
} from '../../interfaces/chat';
import { Markdown } from '../Markdown';
import { OrderSearchResultsBlock } from './OrderSearchResultsBlock';
import { OrderSummaryBlock } from './OrderSummaryBlock';
import { OrderTimelineBlock } from './OrderTimelineBlock';
import { OrderShipmentsBlock } from './OrderShipmentsBlock';
import { NotFoundBlock } from './NotFoundBlock';
import { ClarificationNeededBlock } from './ClarificationNeededBlock';
import { UnsupportedRequestBlock } from './UnsupportedRequestBlock';
import { AgentErrorBlock } from './AgentErrorBlock';

interface ContentBlockRendererProps {
  block: ContentBlock;
}

/**
 * Dispatches a single WISMO order-details agent content block (see
 * wismoagent.md's "Output Contract") to its matching component by `type`.
 * An unrecognized `type` renders nothing — the agent's own contract forbids
 * inventing tags outside the fixed list, so a mismatch here means either a
 * backend bug or a not-yet-supported type, neither of which the UI should
 * guess at rendering.
 */
export function ContentBlockRenderer({ block }: ContentBlockRendererProps) {
  switch (block.type) {
    case 'markdown':
      return <Markdown content={(block as MarkdownBlock).content} />;
    case 'order_search_results':
      return <OrderSearchResultsBlock data={(block.data as OrderSearchResultsData) ?? {}} />;
    case 'order_summary':
      return <OrderSummaryBlock data={(block.data as OrderSummaryData) ?? {}} />;
    case 'order_timeline':
      return <OrderTimelineBlock data={(block.data as OrderTimelineData) ?? {}} />;
    case 'order_shipments':
      return <OrderShipmentsBlock data={(block.data as OrderShipmentsData) ?? {}} />;
    case 'not_found':
      return <NotFoundBlock data={(block.data as NotFoundData) ?? {}} />;
    case 'clarification_needed':
      return <ClarificationNeededBlock data={(block.data as ClarificationNeededData) ?? {}} />;
    case 'unsupported_request':
      return <UnsupportedRequestBlock data={(block.data as UnsupportedRequestData) ?? {}} />;
    case 'error':
      return <AgentErrorBlock data={(block.data as AgentErrorData) ?? {}} />;
    default:
      return null;
  }
}
